// Shop API service.
//
// Talks to the Laravel Admin Shops endpoint so the User App's ShopMap is
// driven by Admin-configured data instead of a hardcoded list. Requests go
// through apiClient so they carry the Dealzta bearer token when one is
// stored (this endpoint doesn't require it today, but is capable of
// sending it consistently with the rest of the app).

import { fetchJson } from './apiClient';

/**
 * Fetch the full shop list from the Laravel Admin API (GET /admin/shops).
 * Resolves to the raw array of shop objects on success (may be empty),
 * throws on a genuine API/network failure.
 */
export async function getShops() {
  const data = await fetchJson('/shops');
  return Array.isArray(data) ? data : [];
}

/**
 * Order shops by Admin's display_order (ties keep the order the array was
 * given in, i.e. a stable sort). Shared by every shop list below so they
 * all order consistently.
 */
function sortByDisplayOrder(shops) {
  return shops
    .map((shop, index) => ({ shop, index }))
    .sort((a, b) => {
      const aOrder = a.shop.display_order;
      const bOrder = b.shop.display_order;

      if (aOrder == null && bOrder == null) return a.index - b.index;
      if (aOrder == null) return 1;
      if (bOrder == null) return -1;
      if (aOrder !== bOrder) return aOrder - bOrder;

      return a.index - b.index;
    })
    .map(({ shop }) => shop);
}

/**
 * Fetch shops and reduce them to what ShopMap needs: only active shops,
 * ordered by Admin's display_order. Every field from the API response is
 * preserved on each shop, so later steps can read address/image_path/etc.
 * without another mapping pass.
 */
export async function getActiveShops() {
  const shops = await getShops();
  return sortByDisplayOrder(shops.filter((shop) => shop.active === true));
}

/**
 * Fetch shops and reduce them to what a single journey's detail screen
 * needs: only active shops belonging to that journey (shop.journey_id ===
 * journeyId), ordered by Admin's display_order.
 */
export async function getActiveShopsForJourney(journeyId) {
  const shops = await getShops();
  return sortByDisplayOrder(
    shops.filter((shop) => shop.active === true && shop.journey_id === journeyId)
  );
}
